import useCanUseNativeLazyLoading from '@charlietango/use-native-lazy-loading';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useInView} from 'react-intersection-observer';
import {preventDefaultEvent} from '../../../common/shared/utils/preventDefaultEvent.function';
import {twMerge} from '../../../common/twMerge';

import {getInternalAssetImage} from '../StaticImage/getInternalAssetImage.function';
import {InternalStaticImageType} from '../StaticImage/InternalStaticImageType.enum';
import {STATIC_IMAGE_INFO_MAP} from '../StaticImage/StaticImageInfoMap.constant';
import {ImageProps} from './ImageProps.type';

export function Image({
  src: _src,
  alt,
  isLazy = false,
  forceUseLazyInView = false,
  showFallbackOnError = false,
  ratio,
  objectFit = 'cover',
  className,
  imageClassName,
  dimmed = false,
  innerLine = false,
  roundPx,
  ...props
}: ImageProps) {
  // 페이지 프로토콜과 동일한 프로토콜(상대 프로토콜, "//")이나 http 프로토콜로 요청하려고 하는 경우가 있다면 https로 바꾸어줍니다.
  // 상대 프로토콜에 대해서도 이제 대부분 권장하지 않는게 중론이므로 이렇게 변경했습니다. 아래 글들을 참고해주세요.
  // https://stackoverflow.com/questions/28446314/why-use-protocol-relative-urls-at-all
  // https://stackoverflow.com/questions/550038/is-it-valid-to-replace-http-with-in-a-script-src-http/54386928#54386928
  const src = useMemo(
    () => _src?.replace(/^(\/\/|http:\/\/)/i, 'https://'),
    [_src]
  );

  const canUseNativeLazyLoading = useCanUseNativeLazyLoading();
  const useNativeLazyLoading = !forceUseLazyInView && canUseNativeLazyLoading; // forceUseLazyInView 옵션이 있다면 무조건 inView 방식을 사용합니다.
  const [showImage, setShowImage] = useState(false);

  const {ref, inView} = useInView({
    triggerOnce: true,
    rootMargin: '200px 0px',
    skip: !isLazy || useNativeLazyLoading,
  });

  useEffect(() => {
    // 한번 보여졌던 lazy loading 이미지는 다시 lazy 상태가 되면 안됩니다. native lazy loading 에는 구현되어있는 기능이지만 inView를 통한 lazy loading은 구현되어있지 않아서 추가로 처리합니다.
    if (showImage) {
      return;
    }

    if (inView || !isLazy || useNativeLazyLoading) {
      setShowImage(true);
    }
  }, [useNativeLazyLoading, inView, isLazy, showImage]);

  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoading, setImageLoading] = useState(true);

  useEffect(() => {
    if (imageRef.current && imageRef.current.complete) {
      setImageLoading(false);
    }
  }, [imageRef]);

  const onImgLoadError = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      if (!showFallbackOnError) {
        return;
      }
      const src = getInternalAssetImage(
        STATIC_IMAGE_INFO_MAP[
          InternalStaticImageType.LoadingImageErrorWebStatic
        ].srcStatic || ''
      );
      const img = event.target as HTMLImageElement;
      if (img.src === src) {
        return;
      }
      img.src = src;
      img.style.objectFit = 'scale-down';
    },
    [showFallbackOnError]
  );

  // 이미지 비율이 지정되어 있지 않다는 건 사실상 우리 페이지에서 컨트롤 할 수 없는 이미지로 봐야 한다. 따라서 별다른 처리 없이 img태그만 리턴해준다.
  if (!ratio) {
    return <img src={src} alt={alt} className={className} {...props} />;
  }

  const {
    crossOrigin,
    srcSet,
    decoding,
    referrerPolicy,
    useMap,
    draggable,
    id,
    onError,
    style,
    ...divProps
  } = props;
  // size 관련 제외한 나머지 Image 관련 props는 img 태그에 전달해준다. size는 div에서 결정되기 때문에 이미지에서는 사이즈를 조작할 수 없도록 함
  const imageProps = {
    crossOrigin,
    srcSet,
    decoding,
    referrerPolicy,
    useMap,
    draggable,
    id,
  };
  const imageSrc = showImage ? src : undefined;

  return (
    <div
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={twMerge('image-container', 'relative', className)}
      ref={ref}
      style={{
        ...style,
        ...(roundPx && {borderRadius: roundPx, overflow: 'hidden'}),
      }}
      {...divProps}
    >
      <div
        className={twMerge(
          'absolute left-0 top-0 flex h-full w-full select-none items-center justify-center bg-[rgb(var(--colors-static-mono-40)_/_15%)]',
          objectFit === 'contain' ? 'object-contain' : 'object-cover',
          imageLoading ? 'block' : 'hidden'
        )}
      />
      <img
        className={twMerge(
          'absolute left-0 top-0 h-full w-full select-none',
          objectFit === 'contain' ? 'object-contain' : 'object-cover',
          imageLoading ? 'invisible' : 'visible',
          imageClassName
        )}
        ref={imageRef}
        src={imageSrc}
        alt={alt}
        loading={isLazy ? 'lazy' : undefined}
        onError={onError ?? onImgLoadError}
        onLoad={() => {
          setImageLoading(false);
        }}
        onSelect={preventDefaultEvent}
        onDragStart={preventDefaultEvent}
        onContextMenu={preventDefaultEvent}
        {...imageProps}
      />
      {dimmed && (
        <div
          className={twMerge(
            'absolute left-0 top-0 h-full w-full select-none bg-[rgb(var(--colors-static-mono-black)_/_50%)]',
            objectFit === 'contain' ? 'object-contain' : 'object-cover'
          )}
        />
      )}
      {innerLine && (
        <div
          className={twMerge('absolute left-0 top-0 h-full w-full select-none')}
          style={{
            ...(roundPx && {borderRadius: roundPx}),
            ...{
              boxShadow:
                '0 0 0 0.5px var(--colors-static-transparent-black-05) inset',
            },
          }}
        />
      )}
      <style jsx>
        {`
          .image-container::after {
            padding-bottom: ${typeof ratio === 'number' ? `${100 * ratio}%` : ratio};
            content: '';
            display: block;
          }
        `}
      </style>
    </div>
  );
}
